<template>
  <iBottomSheet
    v-if="layout === OVERLAY_SHEET"
    :model-value="true"
    :height-percentage="SHEET_HEIGHT_PERCENTAGE"
    avoid-keyboard
    @update:model-value="close"
  >
    <iColumn
      width="fill"
      height="fill"
      gap="none"
      wrap="nowrap"
      overflow="hidden"
      data-test="story-comments-overlay"
      :data-layout="layout"
    >
      <iRow
        align="center"
        width="fill"
        :style-overrides="{paddingTop: 'small', paddingBottom: 'standard'}"
      >
        <iText font-weight="dark" font-size="large" data-test="story-comments-title">
          Comments
        </iText>
      </iRow>
      <iDivider color="light" />
      <iColumn
        width="fill"
        height="fill"
        overflow="auto"
        wrap="nowrap"
        :style-overrides="{paddingTop: 'large', paddingBottom: 'large', paddingLeft: 'large', paddingRight: 'large'}"
        data-test="story-comments-scroller"
        @touchmove.passive="dropKeyboard"
        @wheel.passive="dropKeyboard"
      >
        <StoryCommentThread :story-id="storyId" reply-in-composer @reply-requested="startReply" />
      </iColumn>
      <iDivider color="light" />
      <StoryCommentsOverlayComposer
        ref="composer"
        :story-id="storyId"
        :reply-to="replyTo"
        :initial-segments="composerSegments"
        @stop-reply="stopReply"
      />
    </iColumn>
  </iBottomSheet>
  <iModal
    v-else
    :max-width="layout === OVERLAY_SPLIT ? SPLIT_MAX_WIDTH : SINGLE_MAX_WIDTH"
    :show-header="false"
    :show-footer="false"
    :scrollable="false"
    :focus-first-element="false"
    :backdrop-opacity="BACKDROP_OPACITY"
    :backdrop-blur="0"
    body-height="fill"
    :style-overrides="MODAL_STYLE"
    @close="close"
  >
    <template #modal-wrapper-items>
      <iColumn
        position="fixed"
        :position-placements="{top: CLOSE_INSET, right: CLOSE_INSET, bottom: 'auto', left: 'auto'}"
        width="hug"
      >
        <iButton
          variant="text"
          icon-left="material-symbols:close-rounded"
          aria-label="Close comments"
          :style-overrides="CLOSE_BUTTON_STYLE"
          data-test="story-comments-close"
          @mousedown.stop
          @click="close"
        />
      </iColumn>
    </template>
    <template #body="{maxHeight}">
      <iRow
        width="fill"
        height="fill"
        gap="none"
        wrap="nowrap"
        data-test="story-comments-overlay"
        :data-layout="layout"
      >
        <iColumn
          v-if="layout === OVERLAY_SPLIT"
          width="fill"
          height="fill"
          :min-width="0"
          align="center"
          vertical-align="middle"
          data-test="story-comments-cover"
        >
          <iImageContainer
            :src="story.thumbnail"
            :alt="story.title"
            width="fill"
            :height="maxHeight || 'fill'"
            crop-if-needed
            :style-overrides="{borderRadius: 'none'}"
          />
        </iColumn>
        <iColumn
          :width="layout === OVERLAY_SPLIT ? COLUMN_WIDTH : 'fill'"
          :min-width="layout === OVERLAY_SPLIT ? COLUMN_WIDTH : 'unset'"
          height="fill"
          gap="none"
          wrap="nowrap"
          overflow="hidden"
          data-test="story-comments-column"
        >
          <iRow
            gap="standard"
            vertical-align="middle"
            width="fill"
            wrap="nowrap"
            :style-overrides="PADDED"
          >
            <iAvatar :name="story.authorName" :src="authorAvatar" size="standard" />
            <iText font-weight="dark" font-size="small" data-test="story-comments-author">
              {{ story.authorName }}
            </iText>
          </iRow>
          <iDivider color="light" />
          <iColumn
            width="fill"
            height="fill"
            overflow="auto"
            wrap="nowrap"
            gap="large"
            :style-overrides="PADDED"
            data-test="story-comments-scroller"
          >
            <iRow
              v-if="layout !== OVERLAY_SPLIT"
              gap="standard"
              width="fill"
              vertical-align="top"
              wrap="nowrap"
              data-test="story-comments-caption"
            >
              <iAvatar :name="story.authorName" :src="authorAvatar" size="standard" />
              <iColumn gap="extraSmall" width="fill" :min-width="0">
                <iRow gap="small" vertical-align="middle">
                  <iText font-weight="dark" font-size="small">
                    {{ story.authorName }}
                  </iText>
                  <iText variant="subtle" font-size="small">
                    {{ postedAgo }}
                  </iText>
                </iRow>
                <iText font-weight="dark">
                  {{ story.title }}
                </iText>
                <iText v-if="story.text" :max-vertical-lines="CAPTION_LINES">
                  {{ story.text }}
                </iText>
              </iColumn>
            </iRow>
            <StoryCommentThread :story-id="storyId" reply-in-composer @reply-requested="startReply" />
          </iColumn>
          <iDivider color="light" />
          <iColumn gap="extraSmall" width="fill" :style-overrides="{paddingTop: 'small', paddingLeft: 'large', paddingRight: 'large'}">
            <iRow
              vertical-align="middle"
              width="fill"
              wrap="nowrap"
              gap="standard"
              data-test="story-comments-actions"
            >
              <iButton
                v-if="canLikePosts"
                variant="text"
                :icon-left="liked ? ICONS.liked : ICONS.like"
                :aria-pressed="String(liked)"
                :aria-label="liked ? 'Unlike' : 'Like'"
                icon-left-size="large"
                :style-overrides="ACTION"
                data-test="story-comments-like"
                @click="likePost"
              />
              <iButton
                variant="text"
                :icon-left="ICONS.comment"
                aria-label="Comment"
                icon-left-size="large"
                :style-overrides="ACTION"
                data-test="story-comments-comment"
                @click="focusComposer"
              />
              <iButton
                variant="text"
                :icon-left="sharedAs ? ICONS.shared : ICONS.share"
                aria-label="Share"
                icon-left-size="large"
                :style-overrides="ACTION"
                data-test="story-comments-share"
                @click="sharePost"
              />
              <iRow width="fill" align="right">
                <iButton
                  v-if="canSavePosts"
                  variant="text"
                  :icon-left="saved ? ICONS.saved : ICONS.save"
                  :aria-pressed="String(saved)"
                  :aria-label="saved ? 'Saved' : 'Save'"
                  icon-left-size="large"
                  :style-overrides="ACTION"
                  data-test="story-comments-save"
                  @click="savePost"
                />
              </iRow>
            </iRow>
            <iText
              v-if="canLikePosts"
              font-size="small"
              :font-weight="likeCount > 0 ? 'dark' : 'standard'"
              data-test="story-comments-like-count"
            >
              {{ likeCountLabel }}
            </iText>
            <iText variant="subtle" font-size="verySmall">
              {{ postedAgo }}
            </iText>
          </iColumn>
          <iDivider color="light" />
          <StoryCommentsOverlayComposer
            ref="composer"
            :story-id="storyId"
            :reply-to="replyTo"
            :initial-segments="composerSegments"
            @stop-reply="stopReply"
          />
        </iColumn>
      </iRow>
    </template>
  </iModal>
</template>

<script>
import { mapState } from "pinia";
import App from "@bloglovin/inspire-engine/App";
import { contextualAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useGeneralStore } from "%/dynamic-component-editor/stores/general-store";
import { useSavedPostsStore } from "%/dynamic-component-editor/stores/saved-posts-store";
import { useStoryCommentsStore } from "%/dynamic-component-editor/stores/story-comments-store";
import StoryCommentThread from "%/dynamic-component-editor/components/story-comments/StoryCommentThread.vue";
import StoryCommentsOverlayComposer
  from "%/dynamic-component-editor/components/story-comments/StoryCommentsOverlayComposer.vue";
import { mountSaveChooser } from "%/dynamic-component-editor/components/saved-posts/mount-save-chooser";
import { ENGAGEMENT_ICONS } from "%/dynamic-component-editor/standard-elements/action-element/engagement-action-mixin";
import {
  isLiked,
  engagementFieldsFromStoryEntity,
  shareStory,
  tapStoryLike,
  SHARE_CONFIRMATION_MS,
} from "%/dynamic-component-editor/standard-components/base-story-card/story-engagement-actions";
import {
  OVERLAY_SHEET,
  OVERLAY_SPLIT,
  overlayLayoutFor,
  pictureOrInitials,
  replyPrefillSegments,
  timeAgo,
} from "%/dynamic-component-editor/helpers/story-comments.mjs";

const STORY_ENTITY_TYPE = "Story";
const OVERLAY_SELECTOR = "[data-test=\"story-comments-overlay\"]";
const SHEET_HEIGHT_PERCENTAGE = 85;
const SPLIT_MAX_WIDTH = 1100;
const SINGLE_MAX_WIDTH = 560;
const COLUMN_WIDTH = 440;
const CAPTION_LINES = 6;
const BACKDROP_OPACITY = 0.65;
const CLOSE_INSET = "16px";
const MODAL_STYLE = Object.freeze({
  contentPaddingTop: "none",
  contentPaddingRight: "none",
  contentPaddingBottom: "none",
  contentPaddingLeft: "none",
  modalBorderRadius: "large",
});
const PADDED = Object.freeze({ paddingTop: "standard", paddingRight: "large", paddingBottom: "standard", paddingLeft: "large" });
const ACTION = Object.freeze({
  standardPaddingTop: "extraSmall",
  standardPaddingRight: "none",
  standardPaddingBottom: "extraSmall",
  standardPaddingLeft: "none",
});
const CLOSE_BUTTON_STYLE = Object.freeze({
  textVariantBackgroundColor: "paper",
  textVariantHoverBackgroundColor: "standard",
  textVariantFontColor: "standard",
  textVariantHoverFontColor: "standard",
  borderRadius: "huge",
});

export default {
  name: "StoryCommentsOverlay",
  components: { StoryCommentThread, StoryCommentsOverlayComposer },
  emits: ["closed"],
  data() {
    return {
      viewportWidth: window.innerWidth,
      replyTo: null,
      composerSegments: null,
      sharedAs: "",
      unmountChooser: null,
      OVERLAY_SHEET,
      OVERLAY_SPLIT,
      SHEET_HEIGHT_PERCENTAGE,
      SPLIT_MAX_WIDTH,
      SINGLE_MAX_WIDTH,
      COLUMN_WIDTH,
      CAPTION_LINES,
      BACKDROP_OPACITY,
      CLOSE_INSET,
      MODAL_STYLE,
      CLOSE_BUTTON_STYLE,
      PADDED,
      ACTION,
      ICONS: ENGAGEMENT_ICONS,
    };
  },
  computed: {
    ...mapState(useConfigurationStore, ["canLikePosts", "canSavePosts"]),
    ...mapState(useStoryCommentsStore, ["openStoryId"]),
    storyId() {
      return this.openStoryId;
    },
    story() {
      return App.get(contextualAppName()).getEntity(STORY_ENTITY_TYPE, { id: this.storyId }) ?? {};
    },
    layout() {
      return overlayLayoutFor({ viewportWidth: this.viewportWidth, hasCover: Boolean(this.story.thumbnail) });
    },
    liked() {
      return isLiked(this.story.likeStatus);
    },
    likeCount() {
      return Number(this.story.likeCount ?? 0);
    },
    likeCountLabel() {
      if (this.likeCount === 0) {
        return "Be the first to like this";
      }
      return this.likeCount === 1 ? "1 like" : `${this.likeCount} likes`;
    },
    authorAvatar() {
      return pictureOrInitials(String(this.story.authorAvatar ?? ""));
    },
    postedAgo() {
      const publishedAt = Number(this.story.publishedAt || this.story.timestampCreated || 0);
      return publishedAt ? timeAgo(publishedAt, Math.floor(Date.now() / 1000)) : "";
    },
    saved() {
      return useSavedPostsStore().isSaved(this.storyId);
    },
    choosingCollection() {
      return useSavedPostsStore().chooser.storyId === this.storyId;
    },
  },
  watch: {
    storyId(storyId) {
      if (!storyId) {
        this.$emit("closed");
      }
    },
    layout(next, previous) {
      if (next !== previous) {
        this.keepDraftAcrossLayouts();
      }
    },
    choosingCollection(choosing) {
      if (choosing && !this.unmountChooser) {
        this.unmountChooser = mountSaveChooser(this.$.appContext);
      }
      if (!choosing) {
        this.closeChooser();
      }
    },
  },
  mounted() {
    window.addEventListener("resize", this.measureViewport);
    document.addEventListener("keydown", this.closeOnEscape);
    if (this.canSavePosts) {
      useSavedPostsStore().watchSavedStatus(this.storyId);
    }
    if (this.layout !== OVERLAY_SHEET) {
      this.$nextTick(() => this.focusComposer());
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.measureViewport);
    document.removeEventListener("keydown", this.closeOnEscape);
    this.closeChooser();
  },
  methods: {
    measureViewport() {
      this.viewportWidth = window.innerWidth;
    },
    keepDraftAcrossLayouts() {
      this.composerSegments = this.$refs.composer?.payload() ?? this.composerSegments;
    },
    closeOnEscape(event) {
      if (event.key === "Escape" && this.layout === OVERLAY_SHEET) {
        this.close();
      }
    },
    dropKeyboard() {
      const active = document.activeElement;
      if (active?.closest?.(OVERLAY_SELECTOR)) {
        active.blur();
      }
    },
    close() {
      document.activeElement?.blur?.();
      useStoryCommentsStore().closeOverlay();
    },
    focusComposer() {
      this.$refs.composer?.focus();
    },
    startReply({ parent, answering }) {
      this.replyTo = { parent, answering };
      this.composerSegments = answering.authorName ? replyPrefillSegments({ id: answering.authorId, label: answering.authorName }) : null;
      this.$nextTick(() => this.focusComposer());
    },
    stopReply() {
      this.replyTo = null;
      this.composerSegments = null;
    },
    likePost() {
      tapStoryLike(this.storyId, engagementFieldsFromStoryEntity(this.story));
    },
    sharePost() {
      shareStory(this.storyId, { title: this.story.title, url: `${useGeneralStore().getLink("web")}/story/${this.storyId}` }, outcome => {
        this.sharedAs = outcome;
        setTimeout(() => {
          this.sharedAs = "";
        }, SHARE_CONFIRMATION_MS);
      });
    },
    savePost() {
      useSavedPostsStore().tapSave(this.storyId);
    },
    closeChooser() {
      if (this.unmountChooser) {
        this.unmountChooser();
        this.unmountChooser = null;
      }
    },
  },
};
</script>
